import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Music4, Plus, Play, Edit3, Trash2, Users, Clock, Award, Loader2, Sparkles, Copy } from "lucide-react";

export default function MaestroHome() {
  const [quizzes, setQuizzes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(true);
  const [duplicating, setDuplicating] = useState(null);
  const nav = useNavigate();

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      setBusy(true);
      const [q, t] = await Promise.all([
        api.get("/maestro/quizzes"),
        api.get("/maestro/templates"),
      ]);
      setQuizzes(q.data || []);
      setTemplates(t.data || []);
    } catch (e) {
      if (e.response?.status === 401) nav("/login?next=/play/maestro");
    } finally {
      setBusy(false);
    }
  };

  const launch = async (id) => {
    try {
      const r = await api.post("/maestro/rooms", { quiz_id: id });
      localStorage.setItem(`maestro_host_${r.data.code}`, r.data.host_token);
      nav(`/play/maestro/host/${r.data.code}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur au lancement"); }
  };

  const duplicateTemplate = async (tid) => {
    setDuplicating(tid);
    try {
      const r = await api.post(`/maestro/templates/${tid}/duplicate`);
      toast.success("Modèle dupliqué – vous pouvez le personnaliser.");
      nav(`/play/maestro/edit/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de duplication");
    } finally {
      setDuplicating(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce quiz ?")) return;
    await api.delete(`/maestro/quizzes/${id}`);
    load();
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-10 py-12 md:py-16" data-testid="maestro-home">
      <Toaster theme="light" />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <span className="chip-pink inline-flex items-center gap-1.5"><Music4 className="w-3 h-3" /> Maestro</span>
          <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-3 leading-[1.05]">Vos <span className="text-[#E5276A]">quiz Maestro</span></h1>
          <p className="text-[#4A5568] mt-2 max-w-2xl">Créez vos quiz avec équipes, timer et QR code. Idéal pour dégustations, salons ou cours œno.</p>
        </div>
        <Link to="/play/maestro/new" data-testid="maestro-create-btn"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]">
          <Plus className="w-4 h-4" /> Nouveau quiz
        </Link>
      </div>

      {/* Templates shelf */}
      {templates.length > 0 && (
        <section className="mt-10" data-testid="maestro-templates">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display text-2xl text-[#1F3A5F] inline-flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#F9C548]" /> Modèles prêts à jouer
            </h2>
            <span className="text-xs text-[#8B95A1]">Dupliquez en 1 clic et personnalisez à volonté</span>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {templates.map((t) => (
              <div key={t.id} className="rounded-2xl bg-white border border-[#1F3A5F]/12 p-5 hover:border-[#E5276A]/40 transition-colors flex flex-col"
                data-testid={`maestro-template-${t.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl" style={{ background: `${t.color}18` }}>
                    <span>{t.emoji}</span>
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-lg text-[#1F3A5F] truncate">{t.title}</div>
                    <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: t.color }}>{t.difficulty}</div>
                  </div>
                </div>
                <p className="text-sm text-[#4A5568] mt-3 flex-1">{t.subtitle}</p>
                <div className="flex items-center gap-3 mt-3 text-xs text-[#4A5568]">
                  <span className="inline-flex items-center gap-1"><Award className="w-3 h-3" /> {t.n_questions} Q</span>
                  <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {t.n_teams} équipes</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {t.default_timer_s}s</span>
                </div>
                <button onClick={() => duplicateTemplate(t.id)} disabled={duplicating === t.id}
                  data-testid={`maestro-template-duplicate-${t.id}`}
                  className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870] disabled:opacity-50">
                  {duplicating === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                  Dupliquer & personnaliser
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {busy && <div className="mt-10 text-center text-[#8B95A1]"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>}

      {!busy && quizzes.length === 0 && (
        <div className="mt-10 rounded-2xl border border-dashed border-[#1F3A5F]/20 p-10 text-center">
          <Music4 className="w-10 h-10 text-[#8B95A1] mx-auto" />
          <div className="font-display text-xl text-[#1F3A5F] mt-3">Aucun quiz personnalisé</div>
          <p className="text-sm text-[#4A5568] mt-1">Dupliquez un modèle ci-dessus ou cliquez sur « Nouveau quiz ».</p>
        </div>
      )}

      {!busy && quizzes.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-2xl text-[#1F3A5F] mb-3">Vos quiz</h2>
          <div className="grid md:grid-cols-2 gap-4" data-testid="maestro-quiz-list">
            {quizzes.map((q) => (
              <div key={q.id} className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-5 hover:border-[#E5276A]/40 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-display text-xl text-[#1F3A5F] truncate">{q.title}</h3>
                    {q.subtitle && <p className="text-sm text-[#4A5568] mt-0.5 truncate">{q.subtitle}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-[#4A5568]">
                  <span className="inline-flex items-center gap-1"><Award className="w-3 h-3" /> {(q.questions || []).length} Q</span>
                  <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {(q.teams || []).length} équipes</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {q.default_timer_s}s</span>
                </div>
                <div className="mt-4 flex gap-2 flex-wrap">
                  <button onClick={() => launch(q.id)} data-testid={`maestro-launch-${q.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E5276A] text-white text-xs font-bold hover:bg-[#C01661]">
                    <Play className="w-3 h-3" /> Lancer
                  </button>
                  <Link to={`/play/maestro/edit/${q.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#1F3A5F]/15 text-xs font-bold text-[#1F3A5F] hover:bg-[#FBF7F3]">
                    <Edit3 className="w-3 h-3" /> Éditer
                  </Link>
                  <button onClick={() => remove(q.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-red-600 hover:bg-red-50">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
